/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */
export const preferredVideoMimeTypes = Object.freeze([
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=vp9,opus',
    'video/webm',
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4'
]);

export function selectSupportedVideoMimeType(MediaRecorderCtor = globalThis.MediaRecorder) {
    if (!MediaRecorderCtor || typeof MediaRecorderCtor.isTypeSupported !== 'function') {
        return '';
    }

    return preferredVideoMimeTypes.find(type => MediaRecorderCtor.isTypeSupported(type)) || '';
}

export function getVideoFileExtension(mimeType = '') {
    if (mimeType.includes('mp4')) {
        return 'mp4';
    }

    return 'webm';
}

/**
 * MIME type for GCS object metadata on signed PUT uploads.
 * MediaRecorder types (e.g. video/webm;codecs=vp8,opus) include commas that break HTTP Content-Type headers.
 *
 * @param {string} recorderMimeType
 * @returns {string}
 */
export function toObjectUploadContentType(recorderMimeType = '') {
    const trimmed = String(recorderMimeType).trim();
    if (!trimmed) {
        return 'video/webm';
    }

    const primaryType = trimmed.split(';')[0].trim();
    return primaryType || 'video/webm';
}

/**
 * @param {Record<string, unknown>|null|undefined} storedResponseValue
 * @returns {{ href: string, filename: string }|null}
 */
export function getVideoDownloadTarget(storedResponseValue) {
    if (!storedResponseValue || typeof storedResponseValue !== 'object') {
        return null;
    }

    const href = storedResponseValue.downloadUrl || storedResponseValue.link;
    if (typeof href !== 'string' || !href) {
        return null;
    }

    const storedName = typeof storedResponseValue.name === 'string' ? storedResponseValue.name.trim() : '';
    const mimeType = storedResponseValue.recorderMimeType || storedResponseValue.mime || '';
    const filename = storedName || `video-recording.${getVideoFileExtension(mimeType)}`;

    return { href, filename };
}

/**
 * @param {string} url
 * @param {string} filename
 * @param {{ fetch?: typeof fetch, document?: Document }} [options]
 * @returns {Promise<void>}
 */
export async function downloadRemoteFile(
    url,
    filename,
    { fetch: fetchFn = globalThis.fetch, document: doc = document, URL: urlApi = URL } = {}
) {
    const response = await fetchFn(url);
    if (!response.ok) {
        throw new Error(`Unable to download file (${response.status})`);
    }

    const blob = await response.blob();
    const blobUrl = urlApi.createObjectURL(blob);
    const anchor = doc.createElement('a');

    anchor.href = blobUrl;
    anchor.download = filename;
    anchor.rel = 'noreferrer';
    anchor.style.display = 'none';
    doc.body.appendChild(anchor);
    anchor.click();
    doc.body.removeChild(anchor);
}

export function createVideoRecordingFile({ blob, responseIdentifier, mimeType, recordedAt = new Date() }) {
    const recorderMimeType = mimeType || blob.type || 'video/webm';
    const extension = getVideoFileExtension(recorderMimeType);
    const safeIdentifier = String(responseIdentifier || 'video-response').replace(/[^a-z0-9_-]+/gi, '-');
    const timestamp = recordedAt.toISOString().replace(/[:.]/g, '-');

    const file = new File([blob], `${safeIdentifier}-${timestamp}.${extension}`, {
        type: toObjectUploadContentType(recorderMimeType)
    });

    return { file, recorderMimeType };
}

export function getAverageBitrateKbps(sizeBytes = 0, durationMs = 0) {
    if (sizeBytes <= 0 || durationMs <= 0) {
        return null;
    }

    return Math.round((sizeBytes * 8) / durationMs);
}

export function createRecordingEvidence({
    sizeBytes = 0,
    durationMs = 0,
    mimeType = '',
    uploadDurationMs = null,
    persistenceStrategy = '',
    uploadStrategy = persistenceStrategy
} = {}) {
    const strategy = persistenceStrategy || uploadStrategy;

    return {
        sizeBytes,
        durationMs,
        mimeType,
        uploadDurationMs,
        persistenceStrategy: strategy,
        averageBitrateKbps: getAverageBitrateKbps(sizeBytes, durationMs)
    };
}

export function formatBytes(bytes = 0) {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / 1024 ** power;

    return `${value >= 10 || power === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[power]}`;
}

export function formatDuration(durationMs = 0) {
    durationMs = Number(durationMs);
    if (!Number.isFinite(durationMs)) {
        durationMs = 0;
    }

    const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
